import { z } from 'zod'
import { AccidentFilterSchema, BatchFilterSchema } from './filter'
import { ProviderSchema } from './provider'

export const UserRoleSchema = z.enum([
  'staff',
  'executive',
  'finance',
  'provider',
  'external',
])

export const UserRoleSelectSchema = z.enum([
  'Staff',
  'Executive',
  'Finance',
  'Provider - Staff',
  'Provider - Submitter',
  'External - View Only',
])

export const UserStatusSelectSchema = z.enum(['Active', 'Inactive'])

export const UserSchema = z.object({
  id: z.number(),
  providers: ProviderSchema.array(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  fullName: z.string().nullable(),
  email: z.string(),
  role: UserRoleSchema,
  photoUrl: z.string().nullable(),
  filter: z.object({
    accident: AccidentFilterSchema.optional(),
    batch: BatchFilterSchema.optional(),
  }).nullable(),
  canSubmit: z.boolean(),
  active: z.boolean(),
})

export type UserRole = z.infer<typeof UserRoleSchema>
export type UserRoleSelect = z.infer<typeof UserRoleSelectSchema>
export type UserStatusSelect = z.infer<typeof UserStatusSelectSchema>
export type User = z.infer<typeof UserSchema>

export function statusers(users: User[]) {
  return users.filter((u) => {
    const managerStatusers = ['montana@lienstar.com', 'brad@lienstar.com', 'tyson@lienstar.com']
    return (u.role === 'staff' && u.active) || managerStatusers.includes(u.email)
  })
}

export function userRoleSelect(user: User): UserRoleSelect {
  switch (user.role) {
    case 'staff':
      return 'Staff'
    case 'executive':
      return 'Executive'
    case 'finance':
      return 'Finance'
    case 'provider':
      return user.canSubmit ? 'Provider - Submitter' : 'Provider - Staff'
    case 'external':
      return 'External - View Only'
  }
}

type UserPermissions = {
  canEditData: boolean
  canViewDashboard: boolean
  canViewUsers: boolean
  canViewProviders: boolean
  canViewLawFirms: boolean
  canViewPatientInfo: boolean
  canProxyUsers: boolean
  canFilterByAccidentStatus: boolean
}

export function userPermissions(user: User): UserPermissions {
  switch (user.role) {
    case 'staff':
      return {
        canEditData: true,
        canViewDashboard: true,
        canViewUsers: false,
        canViewProviders: false,
        canViewLawFirms: true,
        canViewPatientInfo: true,
        canProxyUsers: false,
        canFilterByAccidentStatus: true,
      }
    case 'executive':
      return {
        canEditData: true,
        canViewDashboard: true,
        canViewUsers: true,
        canViewProviders: true,
        canViewLawFirms: true,
        canViewPatientInfo: true,
        canProxyUsers: true,
        canFilterByAccidentStatus: true,
      }
    case 'finance':
      return {
        canEditData: true,
        canViewDashboard: true,
        canViewUsers: true,
        canViewProviders: true,
        canViewLawFirms: true,
        canViewPatientInfo: true,
        canProxyUsers: true,
        canFilterByAccidentStatus: true,
      }
    case 'provider':
      return {
        canEditData: true,
        canViewDashboard: false,
        canViewUsers: false,
        canViewProviders: false,
        canViewLawFirms: false,
        canViewPatientInfo: true,
        canProxyUsers: false,
        canFilterByAccidentStatus: true,
      }
    case 'external':
      return {
        canEditData: false,
        canViewDashboard: false,
        canViewUsers: false,
        canViewProviders: false,
        canViewLawFirms: false,
        canViewPatientInfo: false,
        canProxyUsers: false,
        canFilterByAccidentStatus: false,
      }
  }
}
